import axios from 'axios';
import {
  Permission,
  RoleWithPermissions,
  GrantPermissionToRoleRequest,
  RevokePermissionFromRoleRequest,
  BulkGrantPermissionsToRoleRequest,
  BulkRevokePermissionsFromRoleRequest,
} from '@/types/permission';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const permissionService = {
  // Get all permissions
  async getAllPermissions(): Promise<Permission[]> {
    const response = await apiClient.get('/permissions');
    return response.data.permissions;
  },

  // Get permissions by feature
  async getPermissionsByFeature(feature: string): Promise<Permission[]> {
    const response = await apiClient.get(`/permissions/feature/${feature}`);
    return response.data.permissions;
  },

  // Get permissions by role
  async getPermissionsByRole(role: string): Promise<Permission[]> {
    const response = await apiClient.get(`/permissions/role/${role}`);
    return response.data.permissions;
  },

  // Get all roles with their permissions
  async getRolesWithPermissions(): Promise<RoleWithPermissions[]> {
    const response = await apiClient.get('/permissions/roles');
    return response.data.roles;
  },

  // Get available roles
  async getAvailableRoles(): Promise<string[]> {
    const response = await apiClient.get('/permissions/available-roles');
    return response.data.roles;
  },

  // Get user's permissions
  async getUserPermissions(userId: number): Promise<Permission[]> {
    const response = await apiClient.get(`/permissions/user/${userId}`);
    return response.data.permissions;
  },

  // Get all users with their permissions
  async getUsersWithPermissions(): Promise<any[]> {
    const response = await apiClient.get('/permissions/users');
    return response.data.users;
  },

  // Check if user has specific permission
  async checkUserPermission(userId: number, permissionName: string): Promise<boolean> {
    const response = await apiClient.get(`/permissions/user/${userId}/check/${permissionName}`);
    return response.data.hasPermission;
  },

  // Grant permission to role
  async grantPermissionToRole(data: GrantPermissionToRoleRequest): Promise<void> {
    await apiClient.post('/permissions/grant-to-role', data);
  },

  // Revoke permission from role
  async revokePermissionFromRole(data: RevokePermissionFromRoleRequest): Promise<void> {
    await apiClient.post('/permissions/revoke-from-role', data);
  },

  // Bulk grant permissions to role
  async bulkGrantPermissionsToRole(data: BulkGrantPermissionsToRoleRequest): Promise<void> {
    await apiClient.post('/permissions/bulk-grant-to-role', data);
  },

  // Bulk revoke permissions from role
  async bulkRevokePermissionsFromRole(data: BulkRevokePermissionsFromRoleRequest): Promise<void> {
    await apiClient.post('/permissions/bulk-revoke-from-role', data);
  },
}; 